import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { MdClose, MdShoppingCart } from 'react-icons/md';
import { FaPlus, FaMinus } from 'react-icons/fa';
import { motion, AnimatePresence, Variants, useReducedMotion } from 'framer-motion';
import { VegDotIcon } from './DietaryIcon';
import { parsePrice, formatPrice } from '../utils/priceUtils';
import type { ComboItem } from '../types/combo';

// ============================================
// CONSTANTS
// ============================================
const FOOTER_HEIGHT = 80;
const STICKY_HEADER_HEIGHT = 60;

// ============================================
// ANIMATION VARIANTS
// ============================================
const overlayVariants: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

const modalVariants: Variants = {
  hidden: { opacity: 0, scale: 0.95, y: 20 },
  visible: { opacity: 1, scale: 1, y: 0 },
  exit: { opacity: 0, scale: 0.95, y: 20 },
};

const stickyHeaderVariants: Variants = {
  hidden: { opacity: 0, y: -20 },
  visible: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
};

// Spring transition for natural motion
const springTransition = {
  type: 'spring' as const,
  stiffness: 300,
  damping: 30,
};

// ============================================
// TYPES
// ============================================
interface ComboDetailsModalProps {
  isOpen: boolean;
  combo: ComboItem | null;
  onClose: () => void;
  onAddToCart?: (combo: ComboItem, quantity: number) => void;
}

// ============================================
// COMPONENT
// ============================================
export const ComboDetailsModal: React.FC<ComboDetailsModalProps> = React.memo(
  ({ isOpen, combo, onClose, onAddToCart }) => {
    // Accessibility: Respect reduced motion preference
    const shouldReduceMotion = useReducedMotion();
    const animationTransition = shouldReduceMotion ? { duration: 0 } : springTransition;

    const [quantity, setQuantity] = useState(1);
    const [isAddingToCart, setIsAddingToCart] = useState(false);
    const [showStickyHeader, setShowStickyHeader] = useState(false);

    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const imageRef = useRef<HTMLDivElement>(null);

    // Calculate prices (Logic from ComboDetailPage)
    const { individualTotal, comboPrice, offerPercentage } = useMemo(() => {
      if (!combo || !combo.products || !Array.isArray(combo.products)) {
        return { individualTotal: 0, comboPrice: 0, offerPercentage: 0 };
      }
      
      const individual = combo.products.reduce(
        (total: number, product: any) => total + parsePrice(product.price),
        0
      );
      const comboTotal = parsePrice(combo.combo_price);
      const offer = combo.offer_percentage || 0;
      
      return {
        individualTotal: individual,
        comboPrice: comboTotal,
        offerPercentage: offer,
      };
    }, [combo]);

    // Reset when modal opens
    useEffect(() => {
      if (isOpen && combo) {
        setQuantity(1);
        setIsAddingToCart(false);
        setShowStickyHeader(false);
      }
    }, [isOpen, combo]);

    // Handle scroll for sticky header
    useEffect(() => {
      const container = scrollContainerRef.current;
      if (!container || !isOpen) return;

      const handleScroll = () => {
        const scrollTop = container.scrollTop;
        const imageHeight = imageRef.current?.offsetHeight || 200;
        setShowStickyHeader(scrollTop > imageHeight - STICKY_HEADER_HEIGHT);
      };

      container.addEventListener('scroll', handleScroll, { passive: true });
      return () => container.removeEventListener('scroll', handleScroll);
    }, [isOpen]);

    // ESC key close and body scroll lock
    useEffect(() => {
      const handleEscKey = (event: KeyboardEvent) => {
        if (event.key === 'Escape' && isOpen) {
          onClose();
        }
      };
      if (isOpen) {
        document.addEventListener('keydown', handleEscKey);
        document.body.style.overflow = 'hidden';
        return () => {
          document.removeEventListener('keydown', handleEscKey);
          document.body.style.overflow = '';
        };
      }
    }, [isOpen, onClose]);

    // Add to Cart Handler (Logic from ComboDetailPage)
    const handleAddToCartClick = useCallback(async () => {
      if (!combo || !onAddToCart) return;

      setIsAddingToCart(true);
      
      try {
        await onAddToCart(combo, quantity);
        onClose();
      } catch (error) {
        console.error('Error adding combo to cart:', error);
      } finally {
        setIsAddingToCart(false);
      }
    }, [combo, quantity, onAddToCart, onClose]);

    const handleImageError = useCallback(
      (e: React.SyntheticEvent<HTMLImageElement>) => {
        if (combo) {
          (e.target as HTMLImageElement).src = `https://via.placeholder.com/600x400/f8f9fa/6c757d?text=${encodeURIComponent(
            combo.title || 'Combo'
          )}`;
        }
      },
      [combo?.title]
    );

    if (!isOpen || !combo) return null;

    return (
      <AnimatePresence>
        <motion.div
          variants={overlayVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          transition={{ duration: 0.2 }}
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-[99999] p-0 sm:p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="combo-modal-title"
          onClick={onClose}
        >
          <motion.div
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={animationTransition}
            className="bg-white w-full h-full sm:h-auto sm:max-h-[90vh] sm:max-w-lg md:max-w-xl lg:max-w-lg sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Sticky Header - Shows on scroll */}
            <AnimatePresence>
              {showStickyHeader && (
                <motion.div
                  variants={stickyHeaderVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  transition={animationTransition}
                  className="absolute top-0 left-0 right-0 z-20 bg-white border-b border-gray-200 px-4 h-[60px] flex items-center gap-3 shadow-sm"
                >
                  {/* Small Combo Image */}
                  {combo.combo_image && (
                    <img
                      src={combo.combo_image}
                      alt={combo.title}
                      loading="lazy"
                      className="w-8 h-8 rounded-md object-cover flex-shrink-0"
                      onError={handleImageError}
                    />
                  )}
                  <h3 className="font-semibold text-gray-900 truncate text-left flex-1">
                    {combo.title}
                  </h3>
                  {/* Close button */}
                  <button
                    onClick={onClose}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
                    aria-label="Close modal"
                  >
                    <MdClose className="w-5 h-5 text-gray-600" />
                  </button>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Scrollable Content */}
            <div
              ref={scrollContainerRef}
              className="flex-1 overflow-y-auto overscroll-contain"
              style={{ paddingBottom: onAddToCart ? `${FOOTER_HEIGHT}px` : '0px' }}
            >
              {/* Combo Image - Top */}
              {combo.combo_image && (
                <div ref={imageRef} className="relative">
                  <img
                    src={combo.combo_image}
                    alt={combo.title}
                    loading="lazy"
                    className="w-full h-56 sm:h-64 md:h-72 object-cover"
                    fetchPriority="high"
                    onError={handleImageError}
                  />

                  {/* Close button on image */}
                  <button
                    onClick={onClose}
                    className="absolute top-3 right-3 p-2 bg-white/90 hover:bg-white rounded-full shadow-md transition-colors"
                    aria-label="Close modal"
                  >
                    <MdClose className="w-5 h-5 text-gray-700" />
                  </button>
                </div>
              )}

              {/* Content */}
              <div className="p-4 sm:p-5 md:p-6 space-y-4 sm:space-y-5">
                {/* Combo Header */}
                <div>
                  <div className="flex items-start gap-2 mb-2">
                    <h2
                      id="combo-modal-title"
                      className="text-[clamp(1.25rem,4vw,1.875rem)] font-bold text-gray-900 flex-1 leading-tight"
                    >
                      {combo.title}
                    </h2>
                    {/* Close button when no image */}
                    {!combo.combo_image && (
                      <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
                        aria-label="Close modal"
                      >
                        <MdClose className="w-5 h-5 text-gray-700" />
                      </button>
                    )}
                  </div>

                  {combo.description && (
                    <p className="text-gray-700 text-sm sm:text-base leading-relaxed max-w-prose">
                      {combo.description}
                    </p>
                  )}

                  {/* Price Section with Offer Badge */}
                  <div className="mt-3 flex items-center gap-2 sm:gap-3 flex-wrap">
                    {offerPercentage > 0 && (
                      <span className="inline-block px-2.5 py-1 text-xs sm:text-sm font-bold text-white bg-red-500 rounded-full">
                        {offerPercentage}% OFF
                      </span>
                    )}
                    <span className="line-through text-gray-500 text-sm sm:text-base">
                      {formatPrice(individualTotal)}
                    </span>
                    <span className="text-[clamp(1.5rem,5vw,2rem)] font-extrabold text-red-600 leading-none">
                      {formatPrice(comboPrice)}
                    </span>
                  </div>
                </div>

                {/* Included Products */}
                {combo.products && combo.products.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3 text-[clamp(1rem,3vw,1.125rem)]">
                      Included Items ({combo.products.length})
                    </h3>
                    <div className={`space-y-2 sm:space-y-3 ${combo.products.length > 4 ? 'max-h-60 sm:max-h-80 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100' : ''}`}>
                      {combo.products.map((product: any, index: number) => (
                        <div
                          key={index}
                          className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                          {/* Product Image */}
                          <div className="flex-shrink-0">
                            {product.image ? (
                              <img
                                src={product.image}
                                alt={product.name}
                                className="w-14 h-14 sm:w-16 sm:h-16 object-cover rounded-lg"
                                loading="lazy"
                                decoding="async"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).src = `https://via.placeholder.com/64x64/f3f4f6/9ca3af?text=${encodeURIComponent(
                                    product.name.charAt(0)
                                  )}`;
                                }}
                              />
                            ) : (
                              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gray-200 rounded-lg flex items-center justify-center">
                                <span className="text-lg sm:text-xl font-bold text-gray-500">
                                  {product.name.charAt(0)}
                                </span>
                              </div>
                            )}
                          </div>

                          {/* Product Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 sm:gap-2 mb-1">
                              <VegDotIcon item={product} size="sm" />
                              <h4 className="font-medium text-gray-900 text-sm sm:text-base truncate">
                                {product.name}
                              </h4>
                            </div>
                            {product.description && (
                              <p className="hidden sm:block text-xs sm:text-sm text-gray-600 line-clamp-2">
                                {product.description}
                              </p>
                            )}
                            <p className="text-xs sm:text-sm font-medium text-orange-600 mt-1">
                              ${parsePrice(product.price).toFixed(2)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Fixed Footer - Only show if onAddToCart is provided */}
            {onAddToCart && (
              <div
                className="absolute bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg"
                style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)' }}
              >
                <div className="flex items-center gap-3">
                  {/* Quantity Controls */}
                  <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
                    <button
                      onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                      disabled={quantity <= 1}
                      className="w-8 h-8 rounded-md bg-white flex items-center justify-center transition-all disabled:opacity-50"
                      aria-label="Decrease quantity"
                    >
                      <FaMinus className="w-3 h-3 text-gray-600" />
                    </button>
                    <span className="w-10 text-center font-bold text-gray-900">
                      {quantity}
                    </span>
                    <button
                      onClick={() => setQuantity((q) => q + 1)}
                      className="w-8 h-8 rounded-md bg-white flex items-center justify-center transition-all"
                      aria-label="Increase quantity"
                    >
                      <FaPlus className="w-3 h-3 text-gray-600" />
                    </button>
                  </div>

                  {/* Add to Cart Button */}
                  <button
                    onClick={handleAddToCartClick}
                    disabled={isAddingToCart}
                    className={`flex-1 min-h-[48px] sm:min-h-[52px] font-semibold py-3 sm:py-3.5 px-4 rounded-xl transition-all duration-200 flex items-center justify-between ${
                      isAddingToCart
                        ? 'bg-gray-400 cursor-not-allowed text-gray-200'
                        : 'bg-[#FB2B37] text-white shadow-lg hover:shadow-xl hover:bg-[#E01E2B]'
                    }`}
                    aria-label={`Add ${combo.title} to cart for ${formatPrice(comboPrice * quantity)}`}
                  >
                    {isAddingToCart ? (
                      <div className="flex items-center justify-center w-full">
                        <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-2 border-white border-t-transparent mr-2" />
                        Adding...
                      </div>
                    ) : (
                      <>
                        <span className="flex items-center">
                          <MdShoppingCart className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                          Add to Cart
                        </span>
                        <span className="font-bold text-sm sm:text-base">{formatPrice(comboPrice * quantity)}</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  }
);

ComboDetailsModal.displayName = 'ComboDetailsModal';

export default ComboDetailsModal;
